import styled from 'styled-components';
import Image from 'next/image';

interface LinkItem {
  name: string;
  href: string;
}

interface FeatureSectionProps {
  bgColor?: string;
  imageUrl: string;
  imageAlt: string;
  title: string;
  description?: string;
  links: LinkItem[];
}

const FeatureSection: React.FC<FeatureSectionProps> = ({ bgColor, imageUrl, title, description, imageAlt, links }) => {
  return (
    <SectionContainer bgColor={bgColor} aria-label="Feature Section">
      <ContentContainer>
        <ImageBlock aria-label="Feature Image">
          <Image src={imageUrl} alt={imageAlt} layout="fill" objectFit="cover" quality={100} />
        </ImageBlock>
        <TextBlock aria-label="Feature Text">
          <h2>{title}</h2>
          <p>
            Submit your ideas for new features, suggest improvements to the existing ones, or view, vote or comment on
            other peoples ideas to promote these for <span>future implementation.</span>
          </p>
          <LinkList>
            {links.map((link, index) => (
              <LinkItem key={index}>
                <a href={link.href}>{link.name}</a>
                <CircleArrow>
                  <svg width="9" height="15" viewBox="0 0 9 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M8.25666 6.74347C8.67504 7.16185 8.67504 7.84131 8.25666 8.2597L1.83024 14.6861C1.41185 15.1045 0.73239 15.1045 0.314004 14.6861C-0.104383 14.2677 -0.104383 13.5883 0.314004 13.1699L5.98398 7.49991L0.317016 1.82993C-0.101371 1.41155 -0.101371 0.732421 0.317016 0.314034C0.735403 -0.104353 1.41486 -0.104353 1.83325 0.314034L8.25967 6.74045L8.25632 6.7438L8.25666 6.74347Z"
                      fill="white"
                    />
                  </svg>
                </CircleArrow>
              </LinkItem>
            ))}
          </LinkList>
        </TextBlock>
      </ContentContainer>
    </SectionContainer>
  );
};

const ContentContainer = styled.section<{ bgColor?: string }>`
  max-width: 1350px;
  display: flex;
  justify-content: space-between;
  margin: 0 auto;
  column-gap: 200px;
  background-image: url('/wave.png'); /* Path to the wave.png in the public folder */
  background-repeat: no-repeat;
  background-position: center top; /* Adjust as needed */
  background-size: cover; /* Ensure the image covers the container */
`;

const SectionContainer = styled.section<{ bgColor?: string }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${(props) => props.bgColor || '#fff'};
  padding: 40px;
  padding: 200px 0;
`;

const TextBlock = styled.div`
  max-width: 50%;
  font-size: 1.2rem;
  line-height: 1.6;
  max-width: 564px;
  padding-top: 40px;

  h2 {
    font-size: 1.5rem;
    font-family: 'alliance_no.2bold';
    font-weight: 700;
    font-size: 60px;
    line-height: 79.2px;
  }

  p {
    margin: 40px 0;
    font-family: 'Alliance No.2';
    font-weight: 400;
    font-size: 16px;
    line-height: 19px;
    max-width: 450px;

    span {
      font-family: 'alliance_no.2bold';
    }
  }
`;

const ImageBlock = styled.div`
  max-width: 40%;
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  width: 586px;
  height: 586px;
  width: 586px;

  img {
    border-radius: 100px 10px 100px 10px;
  }
`;

const LinkList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
`;

const LinkItem = styled.li`
  width: calc(100% / 2 - 10px);
  position: relative;

  a {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: #fff;
    padding: 15px 20px;
    font-size: 18px;
    font-weight: 600;
    color: #052975;
    text-decoration: none;
    border-radius: 5px;
    box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.1);

    span {
      font-size: 24px;
    }

    &:hover {
      box-shadow: 4px 8px 8px 0px rgba(0, 0, 0, 0.5);
      transition: box-shadow 0.3s ease-in;
    }
  }
`;

const CircleArrow = styled.div`
  width: 33px;
  height: 33px;
  border-radius: 50%;
  background-color: #052975;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: background-color 0.3s ease;
  position: absolute;
  right: 13px;
  top: 50%;
  transform: translateY(-50%);

  /* &:hover {
    background-color: #0056b3;
  } */

  svg {
    width: 9px;
    height: 15px;
    margin-left: 2px;
  }
`;

export default FeatureSection;
